/* CasesPipelineSchedule — Phase 3 of the Aligner Cases redesign (iter98.24).
 *
 * Replaces the "Coming in Phase 3" placeholder with the doctor-suggested
 * 4-card schedule grid translated from the HTML mockup:
 *
 *   1. Today's appointments      — chronological list with type / time / status
 *   2. Aligner changes due       — patients at/near end of current tray
 *   3. This week                 — non-today appointments in next 7 days
 *   4. Needs scheduling          — in-treatment patients with no upcoming visit
 *
 * Apollo cyan-teal palette throughout. Each card has a coloured top accent
 * rail matching its urgency (rose / amber / primary / muted).
 *
 * Data source: GET /api/doctor/schedule-overview (single roundtrip).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    CalendarBlank, CalendarCheck, CalendarPlus, Clock, Warning, ArrowRight,
    ChartLine, ArrowsClockwise, MagnifyingGlass, UserCircle, ChatCircleDots,
    CheckCircle, ArrowSquareOut, Hourglass, Bell,
} from "@phosphor-icons/react";


/* Initials + tint helpers — mirror Pipeline so the same patient shows
 * with consistent avatar styling across views. */
const AVATAR_TINTS = [
    { bg: "bg-primary/15", border: "border-primary/30", text: "text-primary" },
    { bg: "bg-indigo-50",  border: "border-indigo-200", text: "text-indigo-700" },
    { bg: "bg-amber-50",   border: "border-amber-200", text: "text-amber-800" },
    { bg: "bg-rose-50",    border: "border-rose-200", text: "text-rose-700" },
    { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
    { bg: "bg-sky-50",     border: "border-sky-200", text: "text-sky-700" },
];
function avatarTintFor(name) {
    const s = String(name || "?");
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
    return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
}
const initialsOf = (name) =>
    String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";


function formatTime(t) {
    if (!t) return "";
    // Backend stores "HH:MM" (24h). Render as "h:MM a".
    const [h, m] = t.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return t;
    const period = h >= 12 ? "PM" : "AM";
    const hh = ((h + 11) % 12) + 1;
    return `${hh}:${String(m).padStart(2, "0")} ${period}`;
}

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso + "T00:00:00Z");
        return d.toLocaleDateString([], { day: "numeric", month: "short", weekday: "short" });
    } catch { return iso; }
}


export default function CasesPipelineSchedule() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = () => {
        api.get("/doctor/schedule-overview")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => { setLoading(false); setRefreshing(false); });
    };

    useEffect(() => { load(); }, []);

    const refresh = () => { setRefreshing(true); load(); };

    const counts = data?.counts || { today: 0, this_week: 0, aligner_changes_due: 0, needs_scheduling: 0 };

    return (
        <div className="space-y-4" data-testid="cases-schedule-view">
            {/* ── Header bar ── */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <SummaryPill icon={<CalendarCheck size={13} weight="duotone" />} label="Today" value={counts.today} tone="primary" />
                    <SummaryPill icon={<Warning size={13} weight="duotone" />} label="Aligner switches due" value={counts.aligner_changes_due} tone="rose" />
                    <SummaryPill icon={<CalendarBlank size={13} weight="duotone" />} label="This week" value={counts.this_week} tone="amber" />
                    <SummaryPill icon={<Hourglass size={13} weight="duotone" />} label="Needs scheduling" value={counts.needs_scheduling} tone="indigo" />
                </div>
                <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={refresh} disabled={refreshing || loading} data-testid="schedule-refresh-btn">
                    <ArrowsClockwise size={12} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                </Button>
            </div>

            {/* ── 4-card grid ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <TodayCard rows={data?.today || []} loading={loading} navigate={navigate} />
                <AlignerDueCard rows={data?.aligner_changes_due || []} loading={loading} navigate={navigate} />
                <ThisWeekCard rows={data?.this_week || []} loading={loading} navigate={navigate} />
                <NeedsSchedulingCard
                    rows={data?.needs_scheduling || []}
                    total={data?.needs_scheduling_total ?? counts.needs_scheduling}
                    loading={loading}
                    navigate={navigate}
                />
            </div>
        </div>
    );
}


/* ─────────────────────────── SUBCOMPONENTS ─────────────────────────── */

function SummaryPill({ icon, label, value, tone }) {
    const tones = {
        primary: "bg-primary/10 text-primary border-primary/30",
        rose: "bg-rose-50 text-rose-700 border-rose-200",
        amber: "bg-amber-50 text-amber-700 border-amber-200",
        indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    };
    return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${tones[tone]}`}>
            {icon}
            <span>{label}</span>
            <span className="text-[10px] font-bold bg-black/5 px-1.5 py-0.5 rounded-full">{value}</span>
        </div>
    );
}


/* Shared card shell — top accent rail + header + scrollable body. */
function ScheduleCard({ title, icon, tone, count, action, children, testid }) {
    const toneStyles = {
        primary: "bg-primary",
        rose: "bg-rose-500",
        amber: "bg-amber-500",
        indigo: "bg-indigo-500",
        muted: "bg-muted-foreground/40",
    };
    return (
        <div className="relative bg-card border border-border rounded-xl overflow-hidden" data-testid={testid}>
            <span className={`absolute top-0 left-0 right-0 h-[3px] ${toneStyles[tone]}`} />
            <div className="px-4 pt-4 pb-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
                <div className="inline-flex items-center gap-2">
                    {icon}
                    <h3 className="text-sm font-semibold">{title}</h3>
                    <span className="text-[10px] font-bold bg-secondary text-muted-foreground px-1.5 py-0.5 rounded-full tabular-nums">{count}</span>
                </div>
                {action}
            </div>
            <div className="max-h-96 overflow-y-auto">
                {children}
            </div>
        </div>
    );
}


function EmptyRow({ icon, message, hint }) {
    return (
        <div className="py-8 text-center px-6">
            <div className="w-10 h-10 rounded-full bg-secondary grid place-items-center mx-auto mb-2 text-muted-foreground">
                {icon}
            </div>
            <p className="text-[12px] font-medium text-foreground">{message}</p>
            {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
        </div>
    );
}


function TodayCard({ rows, loading, navigate }) {
    return (
        <ScheduleCard
            title="Today's appointments"
            icon={<CalendarCheck size={15} weight="duotone" className="text-primary" />}
            tone="primary"
            count={rows.length}
            action={
                <Button variant="outline" size="sm" className="text-[11px] h-7 rounded-full" onClick={() => navigate("/doctor/appointments")} data-testid="schedule-view-all-today">
                    All appointments <ArrowSquareOut size={11} className="ml-1" />
                </Button>
            }
            testid="schedule-card-today"
        >
            {loading && <div className="px-4 py-6 text-center text-xs text-muted-foreground">Loading…</div>}
            {!loading && rows.length === 0 && (
                <EmptyRow
                    icon={<CalendarCheck size={18} weight="duotone" />}
                    message="No appointments today"
                    hint="Catch up on case reviews or fire compliance nudges."
                />
            )}
            {!loading && rows.length > 0 && (
                <ul className="divide-y divide-border/60">
                    {rows.map((r) => (
                        <li key={r.id} className="px-4 py-3 hover:bg-secondary/40 transition-colors" data-testid={`schedule-today-row-${r.id}`}>
                            <div className="flex items-start gap-3">
                                <div className="shrink-0 w-14 text-center">
                                    <div className="text-base font-bold tabular-nums text-primary leading-none">{formatTime(r.time).split(" ")[0]}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{formatTime(r.time).split(" ")[1]}</div>
                                </div>
                                <PatientAvatar name={r.patient_name} photo={r.patient_photo_data_url} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-medium truncate">{r.patient_name}</div>
                                    <div className="text-[11px] text-muted-foreground truncate">{r.type}</div>
                                    {r.status && (
                                        <span className={`inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                                            r.status === "completed" ? "bg-primary/10 text-primary border-primary/30"
                                                : r.status === "confirmed" || r.status === "scheduled" ? "bg-amber-50 text-amber-700 border-amber-200"
                                                : "bg-secondary text-muted-foreground border-border"
                                        }`}>
                                            {r.status}
                                        </span>
                                    )}
                                </div>
                                {r.case_id && (
                                    <button
                                        onClick={() => navigate(`/doctor/cases/${r.case_id}`)}
                                        className="shrink-0 w-7 h-7 rounded-md border border-border bg-card hover:bg-primary/10 hover:border-primary/40 hover:text-primary text-muted-foreground grid place-items-center transition"
                                        title="Open case"
                                    >
                                        <ArrowSquareOut size={11} />
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </ScheduleCard>
    );
}


function AlignerDueCard({ rows, loading, navigate }) {
    return (
        <ScheduleCard
            title="Aligner changes due"
            icon={<Warning size={15} weight="duotone" className="text-rose-500" />}
            tone="rose"
            count={rows.length}
            testid="schedule-card-aligner-due"
        >
            {loading && <div className="px-4 py-6 text-center text-xs text-muted-foreground">Loading…</div>}
            {!loading && rows.length === 0 && (
                <EmptyRow
                    icon={<CheckCircle size={18} weight="duotone" />}
                    message="No switches due in the next 24 hours"
                    hint="Every active patient is mid-tray."
                />
            )}
            {!loading && rows.length > 0 && (
                <ul className="divide-y divide-border/60">
                    {rows.map((r) => {
                        const toneChip =
                            r.tone === "overdue" ? "bg-rose-50 text-rose-700 border-rose-200"
                            : r.tone === "today" ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-primary/10 text-primary border-primary/30";
                        const toneLabel = r.tone === "overdue" ? "Overdue"
                            : r.tone === "today" ? "Switch today"
                            : "Switch tomorrow";
                        const daysOver = Math.max(0, r.day_of_tray - r.cadence_days);
                        return (
                            <li key={r.case_id} className="px-4 py-3 hover:bg-secondary/40 transition-colors" data-testid={`schedule-aligner-due-row-${r.case_id}`}>
                                <div className="flex items-start gap-3">
                                    <PatientAvatar name={r.patient_name} photo={r.patient_photo_data_url} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-medium truncate">{r.patient_name}</div>
                                        <div className="text-[11px] text-muted-foreground truncate">
                                            Aligner <span className="font-semibold tabular-nums">{r.current_stage}</span>
                                            {r.total_stages > 0 && <> / <span className="tabular-nums">{r.total_stages}</span></>}
                                            {" · "}
                                            Day <span className="font-semibold tabular-nums">{r.day_of_tray}</span> of <span className="tabular-nums">{r.cadence_days}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1.5">
                                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${toneChip}`}>
                                                {toneLabel}
                                            </span>
                                            {daysOver > 0 && (
                                                <span className="text-[10px] text-rose-600 font-medium">
                                                    +{daysOver}d
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => navigate(`/doctor/cases/${r.case_id}`)}
                                        className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-primary/10 hover:bg-primary/15 text-primary border border-primary/30 transition"
                                        title="Open case"
                                        data-testid={`schedule-aligner-due-open-${r.case_id}`}
                                    >
                                        Open <ArrowRight size={10} />
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </ScheduleCard>
    );
}


function ThisWeekCard({ rows, loading, navigate }) {
    // Group by date so the doctor scans days, not raw rows.
    const grouped = useMemo(() => {
        const out = new Map();
        for (const r of rows) {
            const key = r.date;
            if (!out.has(key)) out.set(key, []);
            out.get(key).push(r);
        }
        return Array.from(out.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [rows]);

    return (
        <ScheduleCard
            title="This week"
            icon={<CalendarBlank size={15} weight="duotone" className="text-amber-700" />}
            tone="amber"
            count={rows.length}
            action={
                <Button variant="ghost" size="sm" className="text-[11px] h-7 rounded-full text-muted-foreground hover:text-foreground" onClick={() => navigate("/doctor/appointments")} data-testid="schedule-view-all-week">
                    Open calendar <ArrowSquareOut size={11} className="ml-1" />
                </Button>
            }
            testid="schedule-card-this-week"
        >
            {loading && <div className="px-4 py-6 text-center text-xs text-muted-foreground">Loading…</div>}
            {!loading && grouped.length === 0 && (
                <EmptyRow
                    icon={<CalendarBlank size={18} weight="duotone" />}
                    message="Nothing booked for the rest of the week"
                    hint="A quiet calendar — good time for refinements review."
                />
            )}
            {!loading && grouped.map(([date, dayRows]) => (
                <div key={date}>
                    <div className="px-4 py-2 bg-secondary/60 border-b border-border text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {formatDate(date)} · {dayRows.length} {dayRows.length === 1 ? "appt" : "appts"}
                    </div>
                    <ul className="divide-y divide-border/60">
                        {dayRows.map((r) => (
                            <li key={r.id} className="px-4 py-2.5 hover:bg-secondary/40 transition-colors" data-testid={`schedule-this-week-row-${r.id}`}>
                                <div className="flex items-center gap-3">
                                    <span className="text-[11px] font-semibold tabular-nums text-foreground/80 w-14">
                                        {formatTime(r.time)}
                                    </span>
                                    <PatientAvatar name={r.patient_name} photo={r.patient_photo_data_url} size="sm" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[12px] font-medium truncate">{r.patient_name}</div>
                                        <div className="text-[10px] text-muted-foreground truncate">{r.type}</div>
                                    </div>
                                    {r.case_id && (
                                        <button
                                            onClick={() => navigate(`/doctor/cases/${r.case_id}`)}
                                            className="shrink-0 w-6 h-6 rounded border border-border bg-card hover:bg-primary/10 hover:border-primary/40 hover:text-primary text-muted-foreground grid place-items-center transition"
                                            title="Open case"
                                        >
                                            <ArrowSquareOut size={10} />
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </ScheduleCard>
    );
}


function NeedsSchedulingCard({ rows, total, loading, navigate }) {
    return (
        <ScheduleCard
            title="Needs scheduling"
            icon={<Hourglass size={15} weight="duotone" className="text-indigo-600" />}
            tone="indigo"
            count={total}
            action={
                <Button size="sm" className="text-[11px] h-7 rounded-full" onClick={() => navigate("/doctor/appointments")} data-testid="schedule-book-cta">
                    <CalendarPlus size={11} className="mr-1" /> Book
                </Button>
            }
            testid="schedule-card-needs-scheduling"
        >
            {loading && <div className="px-4 py-6 text-center text-xs text-muted-foreground">Loading…</div>}
            {!loading && rows.length === 0 && (
                <EmptyRow
                    icon={<CheckCircle size={18} weight="duotone" />}
                    message="Every active patient has an upcoming visit"
                    hint="Calendar coverage looks healthy."
                />
            )}
            {!loading && rows.length > 0 && (
                <>
                    <ul className="divide-y divide-border/60">
                        {rows.map((r) => {
                            const silentDays = r.days_since_last_visit;
                            const silentTone = silentDays == null ? "muted"
                                : silentDays > 60 ? "rose"
                                : silentDays > 30 ? "amber"
                                : "primary";
                            return (
                                <li key={r.case_id} className="px-4 py-3 hover:bg-secondary/40 transition-colors" data-testid={`schedule-needs-row-${r.case_id}`}>
                                    <div className="flex items-center gap-3">
                                        <PatientAvatar name={r.patient_name} photo={r.patient_photo_data_url} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-medium truncate">{r.patient_name}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">
                                                Aligner <span className="tabular-nums font-semibold">{r.current_stage}</span>
                                                {" · "}
                                                {silentDays != null ? (
                                                    <span className={
                                                        silentTone === "rose" ? "text-rose-600 font-medium"
                                                        : silentTone === "amber" ? "text-amber-700 font-medium"
                                                        : "text-muted-foreground"
                                                    }>
                                                        {silentDays}d since last visit
                                                    </span>
                                                ) : "no visit on file"}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => navigate(`/doctor/patient-chat?patient=${r.patient_id}`)}
                                                className="shrink-0 w-7 h-7 rounded-md border border-border bg-card hover:bg-primary/10 hover:border-primary/40 hover:text-primary text-muted-foreground grid place-items-center transition"
                                                title="Nudge patient"
                                            >
                                                <ChatCircleDots size={11} />
                                            </button>
                                            <button
                                                onClick={() => navigate(`/doctor/patients/${r.patient_id}`)}
                                                className="shrink-0 w-7 h-7 rounded-md border border-border bg-card hover:bg-secondary text-muted-foreground grid place-items-center transition"
                                                title="Open patient"
                                            >
                                                <UserCircle size={11} />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    {total > rows.length && (
                        <div className="px-4 py-2.5 bg-secondary/30 border-t border-border text-[11px] text-muted-foreground text-center">
                            Showing top {rows.length} of {total} · Sorted by longest silence first.
                        </div>
                    )}
                </>
            )}
        </ScheduleCard>
    );
}


function PatientAvatar({ name, photo, size = "md" }) {
    const tint = avatarTintFor(name);
    const dim = size === "sm" ? "w-7 h-7 text-[10px]" : "w-9 h-9 text-[11px]";
    if (photo) {
        return <img src={photo} alt={name} className={`${dim} rounded-full object-cover border ${tint.border} shrink-0`} />;
    }
    return (
        <div className={`${dim} rounded-full grid place-items-center font-semibold border shrink-0 ${tint.bg} ${tint.border} ${tint.text}`}>
            {initialsOf(name)}
        </div>
    );
}
